import { FunctionComponent } from 'react';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import VerticalContainer from '../../common/VerticalContainer';
import PageContainer from '../../productPage/tableComponents/PageContainer';
import SideBySideSection, { ImageBox } from '../../common/SideBySideSection';
import { Box, Typography } from '@mui/material';
import P1Img from '../../../static/ynex/HVAC.jpg';
import ContentSections from '../../common/ContentSections';
import TextSection from '../../common/TextSection';
import { toUrl } from '@/utils/convertImagePath';

interface OverViewPageProps {}

const IMAGE_HEIGHT = 298; // 與右側圖片高度同步

const titleStyle = {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '100%',
    letterSpacing: '0em',
};

const subTitleStyle = {
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '100%',
    letterSpacing: '0em',
    color: '#B75332',
};

const normalTextStyle = {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '1.3rem',
    letterSpacing: '0em',
};
const P1Title = 'Temperature Monitoring for Electronic Boards';
const P1normalText1 =
    'Thermal management is, and will continue to be, one of the major challenges in electronics. Components are expected to provide more and more power in less and less space. This generates more heat, which must be quickly conducted away. Precise control is required to ensure that the sensitive components maximize performance without overheating and being damaged.';
const P1normalText2 =
    'Precision of data and high reliability are properties that make platinum sensors useful for these applications. But above and beyond this, it is their workability that argues in their favor. With increasing miniaturization, not one micrometer can be wasted. The standardization of contacts for efficient manufacturing is also important.';

const P1fullSectionText1 =
    'Many HVAC applications are in the ambient to low temperature range. Devices using temperature sensors include thermometers, thermostats, smart valves, indoor controls and control units. Furthermore, there are many different equipment types, such as heating furnaces, heat pumps, chillers, cooling towers, air conditioner systems, etc. that are controlled and managed by temperature sensors.';
const P1fullSectionText2 =
    'Different sensor technologies are used in the HVAC environment. Resistance Temperature Detectors (RTDs) and NTCs (Negative Temperature Coefficient) thermistors are most commonly used.';
const P1fullSectionText3 =
    'Platinum temperature sensors (Pt-RTDs) have superior drift behavior, higher long-term stability, better repeatability, and higher temperature capability. Typical RTDs used in HVAC have resistance values of 500 Ohm (Pt500) and 1000 Ohm (PT1000). Contact us for an in-depth comparison of Pt RTDs and NTC sensors.';
const textLists = [
    'High precision',
    'Usability for automatic assembly',
    'Space-saving use on the substrate',
    'High reliability',
    'Economical production in large quantities',
    'Sensors with AECQ 200 certification',
];
const P1fullSectionText4 =
    'The sensor element and specific sensor probe design influence the temperature measurement properties. For many HVAC applications, stainless-steel probe housings are used to protect the Pt element. Fabricating a precision duct or immersion type sensor starts with a wired RTD sensor chip.';

const P2Title = 'Advantages of Platinum-RTDs';
const P2subTitle = 'Cost Reduction';
const P2fullSectionText1 =
    'Pt-RTDs provide high accuracy, low long-term drift and a standardized linear signal. The high measurement repeatability and tight temperature control help reduce the overall error budget in complex and interconnected sensor networks. The excellent  long-term stability and interchangebility of Pt-RTDs reduce re-calibration and repair expenses.';
const P2fullSectionText2 =
    'The linear signal characteristic simplifies the electronic design process and permits less complex sensor electronics. In addition, it supports standardization and allows easy sensor replacement even after many years of use.';

const P2subTitle2 = 'Accurate & Reliable Measurements';
const P2fullSection2Text1 =
    'Pt RTDs are used in furnaces, gas burners and heat pumps, providing accurate measurement and control. Steam and super-heated steam systems depend on the large operating temperature range of Pt-RTDs to provide reliable temperature monitoring. Wood burning pellet stoves operate at high temperatures in order to reduce exhaust gas and CO2 emissions. Pt-RTDs ensure safe operation at temperatures up to 800 °C and beyond, resulting in optimized combustion efficiency. The high temperature capability of Pt-RTDs supports the most demanding applications, under the harshest conditions.\n' +
    'Pt-RTDs also provide accurate and reliable temperature measurement for low-temperature HVAC applications, such as chillers and industrial cooling towers.';
const P2fullSection2Text2 =
    'Precision temperature monitoring and control is essential for smart homes and buildings. The sensors and controls can be stand-alone devices for indoor air and temperature monitoring or part of equipment installations that provide integration and automation functions. The recent trend towards smart buildings drives the need for more and more sensors and integration of equipment within sensor networks. Pt-RTDs help make these networks more reliable, highly accurate and easy to maintain.';

const P2subTitle3 = 'High Accuracy';

const P2fullSection3Text1 =
    'Heat and cold meters have strictly regulated accuracy and service life requirements. Platinum elements provide the high accuracy and long-term stability required to meet the standards of this application. Heat meters use sensor probes that are installed in the inflow and outflow of the water pipes of an apartment/flat. The flow and temperature difference are used to precisely calculate the energy consumed to heat a building unit. SMD Platinum sensors mounted on a PCB are typically used in this application. The PCB design simplifies the probe assembly process and provides thermal decoupling of the SMD sensor from the solder pads and the connection wire. The latter further increases the accuracy of the system.';

const HVACOverViewPage: FunctionComponent<OverViewPageProps> = () => {
    const { isMaxScreen, isScreenXxl, isScreenXl, isScreenLg, isScreenMd, isScreenSm } =
        useResponsive();

    const width = (() => {
        if (isMaxScreen) return '1185px';
        if (isScreenXxl) return '1185px';
        if (isScreenXl) return '1125px';
        if (isScreenLg) return '885px';
        if (isScreenMd) return '650px';
        if (isScreenSm) return '422px';
        return '320px';
    })();

    const variant = (() => {
        if (isMaxScreen) return '60-40';
        if (isScreenXxl) return '60-40';
        if (isScreenXl) return '50-50';
        if (isScreenLg) return '50-50';
        if (isScreenMd) return '50-50';
        if (isScreenSm) return '50-50';
        return '50-50';
    })();

    const variant2 = (() => {
        if (isMaxScreen) return '40-60';
        if (isScreenXxl) return '40-60';
        if (isScreenXl) return '50-50';
        if (isScreenLg) return '50-50';
        if (isScreenMd) return '50-50';
        if (isScreenSm) return '50-50';
        return '50-50';
    })();

    const pic_width = (() => {
        if (isMaxScreen) return '520px';
        if (isScreenXxl) return '520px';
        if (isScreenXl) return '520px';
        if (isScreenLg) return '440px';
        if (isScreenMd) return '650px';
        if (isScreenSm) return '422px';
        return '320px';
    })();

    const sectionsMap = new Map<string, string[]>([
        [P2subTitle, [P2fullSectionText1, P2fullSectionText2]],
        [P2subTitle2, [P2fullSection2Text1, P2fullSection2Text2]],
    ]);

    return (
        <>
            <VerticalContainer
                containerName={'YnexOverview'}
                sx={{
                    padding: 0,
                    margin: 0,
                    height: 'auto',
                    backgroundColor: '#FFFFFF',
                }}
            >
                <PageContainer
                    sx={{
                        paddingTop: '3rem',
                        paddingBottom: '2rem',
                        backgroundColor: '#F2F2F2',
                    }}
                    innerSx={{ width, display: 'flex', flexDirection: 'column', rowGap: '24px' }}
                >
                    {/* 左：等高標題；右：圖片 */}
                    <SideBySideSection
                        containerName="overview_section_1"
                        /** LG / XL 欄距縮小 */
                        compactColumnGapOnLgXl="16px"
                        /** 垂直時圖片在上（右欄優先） */
                        stackedOrder="right-first"
                        variant={variant}
                        alignY="start" // 重要：頂端對齊
                        left={
                            <Box sx={{}} data-testid={'text-area'}>
                                <Typography sx={titleStyle}>{P1Title}</Typography>

                                {/* 若要跟設計圖一樣，段落放在同一個左欄下面 */}
                                <Typography
                                    sx={{
                                        ...normalTextStyle,
                                        paddingTop: '1rem',
                                        paddingBottom: '1rem',
                                    }}
                                >
                                    {P1normalText1}
                                </Typography>
                                <Typography
                                    sx={{
                                        ...normalTextStyle,
                                        paddingTop: '1rem',
                                        paddingBottom: '1rem',
                                    }}
                                >
                                    {P1normalText2}
                                </Typography>
                            </Box>
                        }
                        right={
                            <ImageBox
                                src={toUrl(P1Img)}
                                alt="overview"
                                sx={{
                                    height: IMAGE_HEIGHT, // 固定圖片高度
                                    width: pic_width,
                                }}
                            />
                        }
                    />

                    {/* 你要的：在 PageContainer 內的純文字區塊（不獨立成組件） */}
                    <Box
                        sx={{
                            backgroundColor: '#F2F2F2',
                            paddingRight: '0px',
                            paddingBottom: '1rem',
                            paddingLeft: '0px',
                        }}
                    >
                        <Typography sx={normalTextStyle}>{P1fullSectionText1}</Typography>
                        <Typography
                            sx={{ ...normalTextStyle, paddingTop: '1rem', paddingBottom: '0.5rem' }}
                        >
                            {P1fullSectionText2}
                        </Typography>
                        <Typography
                            sx={{
                                ...normalTextStyle,
                                paddingTop: '0.5rem',
                                paddingBottom: '0.5rem',
                            }}
                        >
                            {P1fullSectionText3}
                        </Typography>
                        <Typography sx={{ ...normalTextStyle, paddingTop: '0.5rem' }}>
                            {P1fullSectionText4}
                        </Typography>
                    </Box>
                </PageContainer>
                <ContentSections
                    containerName={'p2'}
                    title={P2Title}
                    sections={sectionsMap}
                    innerSx={{ width }}
                    containerSx={{}}
                />
                <PageContainer
                    containerName={'overview_section_2'}
                    sx={{
                        paddingTop: '0',
                        paddingBottom: '0',
                        backgroundColor: 'white',
                    }}
                    innerSx={{ width, display: 'flex', flexDirection: 'column', rowGap: '24px' }}
                >
                    <SideBySideSection
                        containerName="overview_section_2"
                        /** LG / XL 欄距縮小 */
                        compactColumnGapOnLgXl="16px"
                        /** 垂直時圖片在上（右欄優先） */
                        stackedOrder="right-first"
                        variant={'50-50'}
                        alignY="start" // 重要：頂端對齊
                        left={
                            <ImageBox
                                src={toUrl(P1Img)}
                                alt="overview"
                                sx={{
                                    height: IMAGE_HEIGHT, // 固定圖片高度
                                    width: pic_width,
                                }}
                            />
                        }
                        right={
                            <TextSection
                                testId={'P2subTitle3'}
                                subTitle={P2subTitle3}
                                texts={[P2fullSection3Text1]}
                            />
                        }
                    />
                </PageContainer>
                <Box sx={{ width: width, backgroundColor: '#FFFFFF', height: '1rem' }}></Box>
            </VerticalContainer>
        </>
    );
};

export default HVACOverViewPage;
